import { Box, Button, Icon, SpaceBetween } from '@amzn/awsui-components-core';
import LoadingBar from '@amzn/awsui-chat-components-core/loading-bar';
import React, { useEffect, useRef, useState } from 'react';

import { logger } from 'src/logger';
import { buildNarrativeCacheKey, clearCachedNarrative, getCachedNarrative, setCachedNarrative } from './narrativeCache';
import styles from './aiNarrative.module.scss';

/**
 * Client-side ceiling so loading can never hang. Does not cancel the in-flight request
 * — only stops us waiting on it.
 */
const NARRATIVE_TIMEOUT_MS = 120000;

/**
 * Recognises an expired/absent auth session, which a retry cannot resolve — the page has
 * to be reloaded, so offering Retry would mislead.
 */
const isAuthSessionError = (message: string): boolean => /no id token|auth session|expired|not authorized|unauthenticated/i.test(message);

interface AiNarrativeProps {
  /** Stable key identifying this narrative, used for the cache key and logging. */
  narrativeKey: string;
  /**
   * Current context (JSON string). The narrative regenerates when this changes, and it
   * participates in the cache key so different contexts never share a cached result.
   */
  filters: string;
  /** Generates the narrative. Invoked when `ready` becomes true and on regenerate. */
  generate: () => Promise<string>;
  /** Only fetch once the underlying data is ready. */
  ready: boolean;
  /** Header label. Defaults to "AI Summary". */
  title?: string;
  /** Reports the displayed narrative up (e.g. so a parent can export or mirror it). */
  onSummaryChange?: (text: string) => void;
}

/**
 * AiNarrative — reusable, read-only AI-generated summary with regenerate.
 *
 * Auto-generates once `ready`, caches the result per `narrativeKey + filters`, and lets
 * the user regenerate. Unlike the OE Report's `SectionNarrative`, there is no editing,
 * saving, or persistence — this is the lightweight read-only variant used where a
 * narrative is derived live and not stored (e.g. the KPI Metrics dashboards).
 */
export const AiNarrative: React.FC<AiNarrativeProps> = ({ narrativeKey, filters, generate, ready, title = 'AI Summary', onSummaryChange }) => {
  const [summary, setSummary] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  /** Set when the failure is an expired auth session, which a retry cannot resolve. */
  const [authExpired, setAuthExpired] = useState<boolean>(false);
  const [reloadTick, setReloadTick] = useState<number>(0);

  // "Latest ref" pattern: callers rebuild `generate` whenever their data changes, so
  // depending on it directly would regenerate on every data change. Assigned during
  // render so the effect always calls the current value without taking it as a dep.
  const generateRef = useRef(generate);
  generateRef.current = generate;

  // Changing filters starts a new context, so drop any in-progress "regenerating" mode
  // and let the effect run fresh against the new filters. `filtersRef` lets the generate
  // effect detect it is running against a filter set the reset has not applied yet (both
  // effects run in the same commit) and skip that redundant pass.
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
    setReloadTick(0);
  }, [filters]);

  // Report the displayed narrative up. In an effect so the parent is never updated during
  // this component's render. Gated on `loading` so a stale narrative is not reported while
  // a regeneration is in flight.
  const onSummaryChangeRef = useRef(onSummaryChange);
  onSummaryChangeRef.current = onSummaryChange;
  useEffect(() => {
    onSummaryChangeRef.current?.(loading ? '' : summary);
  }, [summary, loading]);

  // Generate (or regenerate) via the provided callback, backed by the module cache.
  useEffect(() => {
    if (!ready || !narrativeKey) return;

    // Filters changed but the reset above has not landed yet, so `reloadTick` is stale.
    // Skip this pass; the reset re-runs the effect with the correct value.
    if (filtersRef.current !== filters && reloadTick !== 0) return;

    // Per-run flag: generation takes seconds, so a filter change or a second regenerate
    // click can leave an older request in flight. Without this the slower response would
    // land last and overwrite the newer narrative.
    let cancelled = false;

    const cacheKey = buildNarrativeCacheKey(narrativeKey, filters);

    const run = async () => {
      if (reloadTick === 0) {
        const cached = getCachedNarrative(cacheKey);
        if (cached) {
          if (cancelled) return;
          setSummary(cached);
          setLoading(false);
          setError(false);
          return;
        }
      }
      try {
        // Clear prior failure state up front so a repeat attempt visibly restarts.
        setLoading(true);
        setError(false);
        setErrorMessage('');
        setAuthExpired(false);

        const message = await Promise.race([
          generateRef.current(),
          new Promise<never>((_, rejectTimeout) => setTimeout(() => rejectTimeout(new Error('Narrative generation timed out')), NARRATIVE_TIMEOUT_MS))
        ]);
        if (cancelled) return;
        setSummary(message);
        setCachedNarrative(cacheKey, message);
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        // Logged even when superseded, so a genuine failure is never hidden.
        logger.error(`AiNarrative summary failed for ${narrativeKey}: ${e.message}`, e);
        if (cancelled) return;
        setErrorMessage(e.message);
        setAuthExpired(isAuthSessionError(e.message));
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // `generate` is read via a ref, not listed: regeneration is driven by `reloadTick`
    // and `filters`; depending on `generate` would re-fire for the same click.
  }, [ready, narrativeKey, filters, reloadTick]);

  /** Regenerate. Guarded so a click while a request is in flight is ignored. */
  const regenerate = () => {
    if (loading) return;
    // Drop any cached entry so the regenerate genuinely re-invokes rather than serving
    // the cached text, and guarantee `reloadTick > 0` so the cache-read branch is skipped.
    clearCachedNarrative(buildNarrativeCacheKey(narrativeKey, filters));
    setReloadTick((t) => t + 1);
  };

  return (
    <div className={styles.narrative}>
      <div className={styles.narrativeHeader}>
        <Icon name="gen-ai" />
        <span>{title}</span>
        <span className={styles.narrativeActions}>
          <Button variant="inline-icon" iconName="refresh" ariaLabel="Regenerate summary" onClick={regenerate} disabled={loading} />
        </span>
      </div>

      {loading ? (
        <div className={styles.narrativeLoading}>
          <Box>Generating summary…</Box>
          <div className="ai-summary-loading-bar">
            <LoadingBar variant="gen-ai" />
          </div>
        </div>
      ) : error ? (
        <SpaceBetween size="xs" direction="horizontal">
          {authExpired ? (
            <>
              <Box color="text-status-error">Your session expired, so the summary could not be generated.</Box>
              <Button variant="inline-link" onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </>
          ) : (
            <>
              <Box color="text-status-error">Unable to generate summary{errorMessage ? `: ${errorMessage}` : '.'}</Box>
              <Button variant="inline-link" onClick={regenerate}>
                Retry
              </Button>
            </>
          )}
        </SpaceBetween>
      ) : (
        /* Rendered as text, never HTML: the narrative is always plain text from the
           model, so there is no markup to interpret and no need for an HTML sink. */
        <div className={styles.narrativeText}>{summary}</div>
      )}
    </div>
  );
};
